import { useEffect } from 'react';

/**
 * Lightweight per-page SEO hook.
 *
 * Updates <title>, <meta name="description">, and Open Graph / Twitter equivalents
 * so each route on the SPA has distinct metadata for crawlers that follow client-
 * side navigation (Google, Bing, and modern SEO / LLM bots).
 *
 * Called at the top of each page component:
 *   useSEO({ title: '...', description: '...', path: '/about' });
 */
export function useSEO({ title, description, path = '', image }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, name, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const canonical = `https://keystoneconsulting.uk${path}`;
    setMeta('name', 'description', description);
    setMeta('name', 'title', title);

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    if (image) setMeta('property', 'og:image', image);

    setMeta('property', 'twitter:title', title);
    setMeta('property', 'twitter:description', description);
    setMeta('property', 'twitter:url', canonical);
    if (image) setMeta('property', 'twitter:image', image);

    // Canonical link tag
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, [title, description, path, image]);
}
